import {
  CalendarDateTime,
  fromDate,
  getLocalTimeZone,
  toCalendarDateTime,
} from "@internationalized/date";
import { useEffect, useState } from "react";
import { Key } from "react-aria";
import { twMerge } from "tailwind-merge";
import { DatePicker } from "./DatePicker";
import { NumberField } from "./NumberField";
import { Radio, RadioGroup } from "./RadioGroup";
import { Select, SelectItem } from "./Select";

interface AdvancedDateTimeFieldProps {
  className?: string;
  name: string;
  label: string;
  value?: string | null;
  description?: string;
  onChange?: (value: string | null) => void;
  errorMessage?: string;
}

const timeUnits = [
  { value: "minute", label: "minutter" },
  { value: "hour", label: "timer" },
  { value: "day", label: "dage" },
  { value: "week", label: "uger" },
  { value: "month", label: "måneder" },
  { value: "year", label: "år" },
];

const timeUnitByValue = new Map(
  timeUnits.map((unit) => [unit.value, unit.label]),
);

const formatPreciseDateTime = (date: CalendarDateTime) => {
  return date.toDate(getLocalTimeZone()).toISOString();
};

const formatRelativeTime = (amount: number, unit: Key) => {
  return `${amount} ${timeUnits.find((t) => t.value === unit)?.value}`;
};

export const AdvancedDateTimeField = ({
  className,
  name,
  label,
  value,
  description,
  onChange,
  errorMessage,
}: AdvancedDateTimeFieldProps) => {
  const [usePreciseTime, setUsePreciseTime] = useState(true);
  const [relativeUnit, setRelativeUnit] = useState<Key | null>(null);
  const [relativeNumber, setRelativeNumber] = useState<number | null>(null);
  const [preciseDateTime, setPreciseDateTime] =
    useState<CalendarDateTime | null>(null);

  useEffect(() => {
    if (!value) return;
    if (timeUnits.some((t) => value.includes(t.value))) {
      setUsePreciseTime(false);
      const [number, unit] = value.split(" ");
      setRelativeNumber(parseInt(number, 10));
      setRelativeUnit(unit as Key);
    } else {
      try {
        const date = new Date(value);
        setUsePreciseTime(true);
        setPreciseDateTime(
          toCalendarDateTime(fromDate(date, getLocalTimeZone())),
        );
      } catch {
        // ignore
      }
    }
  }, [value]);

  useEffect(() => {
    if (usePreciseTime) {
      if (preciseDateTime !== null) {
        const newValue = formatPreciseDateTime(preciseDateTime);
        if (newValue !== value) onChange?.(newValue);
        return;
      }
    } else {
      if (relativeNumber !== null && relativeUnit) {
        const newValue = formatRelativeTime(relativeNumber, relativeUnit);
        if (newValue !== value) onChange?.(newValue);
        return;
      }
    }
    if (!value) return;
    onChange?.(null);
  }, [preciseDateTime, relativeNumber, relativeUnit, usePreciseTime]);

  return (
    <div className={twMerge("flex flex-col gap-y-6", className)}>
      <div>
        <label className="text-theme-on-background-variant text-sm font-medium">
          {label}
        </label>
        {description && (
          <p className="text-theme-on-background-variant mt-1 text-xs">
            {description}
          </p>
        )}
      </div>
      <div>
        {/* Radio buttons for time type selection */}
        <div className="mb-6 flex gap-6">
          <RadioGroup
            border={false}
            onChange={(value) => {
              if (value === "true") {
                setRelativeNumber(null);
                setRelativeUnit(null);
              } else {
                setPreciseDateTime(null);
                setRelativeUnit("minute");
              }
              setUsePreciseTime(value === "true");
            }}
            value={usePreciseTime ? "true" : "false"}
            defaultValue={usePreciseTime ? "true" : "false"}
          >
            <Radio value="false" className="text-nowrap">
              Relativt tidspunkt
            </Radio>
            <Radio value="true" className="text-nowrap">
              Præcist tidspunkt
            </Radio>
          </RadioGroup>
        </div>

        {/* Relative time section */}
        {usePreciseTime ? (
          <DatePicker
            name={name}
            onChange={setPreciseDateTime}
            value={preciseDateTime}
            shouldForceLeadingZeros
            hideTimeZone
            aria-label="Felt for dato og tid"
            granularity="minute"
          />
        ) : (
          <div className="flex items-center gap-2">
            <span className="text-theme-on-background text-sm">Om</span>
            <div className="w-32">
              <NumberField
                placeholder="Indtast"
                aria-label="Tidsenhed"
                minValue={0}
                value={relativeNumber !== null ? relativeNumber : null}
                onChange={(value) => setRelativeNumber(value)}
              />
            </div>
            <div className="w-32">
              <Select
                items={timeUnits}
                selectedKey={relativeUnit}
                defaultSelectedKey={"minute"}
                onSelectionChange={setRelativeUnit}
                placeholder="Vælg enhed"
                aria-label="Tidsenhed"
              >
                {(item) => (
                  <SelectItem key={item.value} id={item.value}>
                    {item.label}
                  </SelectItem>
                )}
              </Select>
            </div>
          </div>
        )}

        {errorMessage && (
          <div className="mt-3 ml-[5px] text-[11px] text-red-600">
            {errorMessage}
          </div>
        )}
      </div>
    </div>
  );
};
